import { z } from "zod";
import type { AdminTeamRow, MePayload, TeamInviteRow } from "@/lib/types";

export const steps = ["team", "season", "invite", "done"] as const;
export const staffRoles = ["coach", "assistant_coach", "team_admin"] as const;
export const roleLabels = {
  coach: "Coach",
  assistant_coach: "Assistant coach",
  team_admin: "Team admin",
};
export function validCalendarDate(value: string) {
  if (!value) return true;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(`${value}T00:00:00Z`);
  return (
    year > 0 &&
    date.getUTCFullYear() === year &&
    date.getUTCMonth() + 1 === month &&
    date.getUTCDate() === day
  );
}
export const teamSchema = z.object({
  name: z.string().trim().min(1, "Enter a team name.").max(200),
  slug: z
    .string()
    .trim()
    .max(64)
    .regex(
      /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/,
      "Use lowercase letters, numbers and hyphens.",
    )
    .refine((v) => v !== "default-team", "Choose a different team URL."),
  game_format: z.enum(["7v7", "9v9", "11v11"]),
});
export const seasonSchema = z
  .object({
    name: z.string().trim().min(1, "Enter a season name.").max(200),
    starts_on: z
      .string()
      .refine(validCalendarDate, "Enter a valid calendar date."),
    ends_on: z
      .string()
      .refine(validCalendarDate, "Enter a valid calendar date."),
  })
  .refine((v) => !v.starts_on || !v.ends_on || v.starts_on <= v.ends_on, {
    message: "End date must be on or after the start date.",
    path: ["ends_on"],
  });
export const inviteSchema = z.object({
  email: z
    .email("Enter a valid email address.")
    .transform((v) => v.toLowerCase()),
  role: z.enum(staffRoles),
});
const pendingSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("team"),
    body: teamSchema,
    before: z.array(z.string()),
  }),
  z.object({
    kind: z.literal("season"),
    body: seasonSchema,
    before: z.array(z.string()),
  }),
  z.object({
    kind: z.literal("invite"),
    body: inviteSchema.extend({
      team_id: z.string(),
      season_id: z.string().optional(),
    }),
    before: z.array(z.string()),
  }),
]);
const draftSchema = z.object({
  team: z.object({
    name: z.string(),
    slug: z.string(),
    game_format: z.enum(["7v7", "9v9", "11v11"]),
  }),
  season: z.object({
    name: z.string(),
    starts_on: z.string(),
    ends_on: z.string(),
  }),
  invite: z.object({ email: z.string(), role: z.enum(staffRoles) }),
  seasonMode: z.enum(["new", "existing"]),
  existingSeasonId: z.string(),
  slugEdited: z.boolean(),
});
const stateSchema = z.object({
  version: z.literal(1),
  step: z.enum(steps),
  dismissed: z.boolean(),
  completed: z.boolean(),
  teamId: z.string().nullable(),
  seasonId: z.string().nullable(),
  draft: draftSchema,
  pending: pendingSchema.nullable(),
  // Retain skipped uncertain requests so returning to a form cannot resend them.
  skippedWrites: z.array(pendingSchema).default([]),
});
export type OnboardingState = z.infer<typeof stateSchema>;
export type PendingWrite = z.infer<typeof pendingSchema>;
export const newState = (): OnboardingState => ({
  version: 1,
  step: "team",
  dismissed: false,
  completed: false,
  teamId: null,
  seasonId: null,
  pending: null,
  skippedWrites: [],
  draft: {
    team: { name: "", slug: "", game_format: "11v11" },
    season: { name: "", starts_on: "", ends_on: "" },
    invite: { email: "", role: "coach" },
    seasonMode: "new",
    existingSeasonId: "",
    slugEdited: false,
  },
});
export function setupKey(userId: string, clubId: string) {
  return `tktic_club_setup:v1:${encodeURIComponent(userId)}:${encodeURIComponent(clubId)}`;
}
const memory = new Map<string, OnboardingState>();
export function readSetup(key: string): OnboardingState {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = stateSchema.safeParse(JSON.parse(raw));
      if (parsed.success) return parsed.data;
    }
  } catch {
    /* Storage unavailable: retain this tab's scoped progress. */
  }
  return memory.get(key) ?? newState();
}
export function writeSetup(key: string, state: OnboardingState) {
  // Project explicitly through the schema: never persist API responses, tokens or secrets.
  const safe = stateSchema.parse(state);
  memory.set(key, safe);
  try {
    localStorage.setItem(key, JSON.stringify(safe));
  } catch {
    /* In-memory fallback. */
  }
  window.dispatchEvent(new CustomEvent("tktic-club-setup", { detail: key }));
}
export function isBootstrapTeam(team: AdminTeamRow) {
  return team.id === "default-team" && team.slug === "default-team";
}
export function isPendingSetupInvite(
  invite: TeamInviteRow,
  nowMs = Date.now(),
) {
  return (
    invite.status === "pending" &&
    (invite.expires_at == null || invite.expires_at * 1000 > nowMs)
  );
}
export function sameSetupRequest(a: PendingWrite, b: PendingWrite) {
  if (a.kind === "team" && b.kind === "team")
    return a.body.slug === b.body.slug;
  if (a.kind === "season" && b.kind === "season")
    return (
      a.body.name === b.body.name &&
      a.body.starts_on === b.body.starts_on &&
      a.body.ends_on === b.body.ends_on
    );
  if (a.kind === "invite" && b.kind === "invite")
    return a.body.team_id === b.body.team_id && a.body.email === b.body.email;
  return false;
}
export function samePendingSetupWrite(a: PendingWrite, b: PendingWrite) {
  return (
    JSON.stringify(pendingSchema.parse(a)) ===
    JSON.stringify(pendingSchema.parse(b))
  );
}
export function canSetUpClub(me: MePayload | undefined, host: string) {
  return !!(
    me?.user &&
    host === "club" &&
    me.host_kind === "club" &&
    me.current_club?.status === "active" &&
    me.is_club_admin
  );
}
export function slugFromName(name: string) {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64)
    .replace(/-+$/g, "");
}

/** Serialize the same user's setup across tabs. The server remains the
 * authority for different admins and club/team authorization. */
export async function withSetupLock<T>(
  key: string,
  operation: () => Promise<T>,
): Promise<T> {
  if (!navigator.locks) return operation();
  return navigator.locks.request(key, { ifAvailable: true }, (lock) => {
    if (!lock)
      throw new Error(
        "Setup is being saved in another tab. Wait for it to finish, then check saved records.",
      );
    return operation();
  });
}
